import { useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { FiMail } from 'react-icons/fi';
import { MessageMedia, isMediaMessage } from './MessageMedia';
import './ChatWidget.css';

function MessageList({
  messages,
  currentUserId,
  canSendManualEmail = false,
  onSendNotification,
  notifyLoading = false,
  notifiedMessageIds = [],
}) {
  const messagesEndRef = useRef(null);
  const prevLengthRef = useRef(0);
  const history = useHistory();

  // Scroll-to-bottom strategy: jump instantly on the initial load
  // / conversation switch (so opening a long thread doesn't animate
  // a screenful of messages past the eye), smooth-scroll only when
  // a single new message appends to the current thread. Length
  // delta of exactly +1 is the "new message arrived" signal;
  // anything else (0 → N on mount, N → M on switch, bulk catch-up
  // after reconnect) is treated as a load and jumps without
  // animation.
  useEffect(() => {
    const prev = prevLengthRef.current;
    const next = messages.length;
    prevLengthRef.current = next;
    if (!messagesEndRef.current) return;
    const isAppend = next === prev + 1 && prev > 0;
    messagesEndRef.current.scrollIntoView({
      behavior: isAppend ? 'smooth' : 'auto',
      block: 'end',
    });
  }, [messages]);

  const formatTime = (date) => {
    return new Date(date).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Check if URL is from the same app
  const isInternalLink = (url) => {
    try {
      const urlObj = new URL(url);
      const currentOrigin = window.location.origin;
      return urlObj.origin === currentOrigin;
    } catch (e) {
      return false;
    }
  };

  // URL detection and link formatting
  const formatMessageText = (text) => {
    // URL regex pattern
    const urlPattern = /(https?:\/\/[^\s]+)/g;
    const parts = String(text || '').split(urlPattern);

    return parts.map((part, index) => {
      if (urlPattern.test(part)) {
        const isInternal = isInternalLink(part);

        if (isInternal) {
          // Extract pathname from full URL for navigation
          try {
            const urlObj = new URL(part);
            const path = urlObj.pathname + urlObj.search + urlObj.hash;

            return (
              <a
                key={index}
                href={part}
                onClick={(e) => {
                  e.preventDefault();
                  history.push(path);
                }}
                style={{
                  color: '#06b6d4',
                  textDecoration: 'underline',
                  wordBreak: 'break-all',
                  cursor: 'pointer',
                }}
              >
                {part}
              </a>
            );
          } catch (e) {
            // Fallback to regular link if URL parsing fails
            return (
              <a
                key={index}
                href={part}
                style={{
                  color: '#06b6d4',
                  textDecoration: 'underline',
                  wordBreak: 'break-all',
                }}
              >
                {part}
              </a>
            );
          }
        } else {
          // External link - open in new tab
          return (
            <a
              key={index}
              href={part}
              target='_blank'
              rel='noopener noreferrer'
              style={{
                color: '#06b6d4',
                textDecoration: 'underline',
                wordBreak: 'break-all',
              }}
            >
              {part}
            </a>
          );
        }
      }
      return part;
    });
  };

  return (
    <div className='message-list'>
      {messages.map((msg) => {
        const isOwnMessage = msg.senderId === currentUserId;
        const canEmailThisMessage = canSendManualEmail && isOwnMessage;
        const messageId = msg._id || msg.tempId;
        const isMessageNotified =
          messageId && notifiedMessageIds.includes(messageId);
        const tooltip = !canSendManualEmail
          ? "You don't have permission to send manual notifications."
          : notifyLoading
          ? 'Sending email...'
          : isMessageNotified
          ? 'This message was already emailed. Click to resend.'
          : 'Send email reminder for this message';

        return (
          <div
            key={msg._id}
            className={`message-item ${isOwnMessage ? 'own' : 'other'}`}
          >
            {!isOwnMessage && msg.sender?.avatar && (
              <img
                src={msg.sender.avatar}
                alt={msg.senderName || 'User'}
                style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  objectFit: 'cover',
                  marginRight: '8px',
                }}
              />
            )}
            <div
              className={`message-bubble ${isOwnMessage ? 'own' : 'other'}${
                isMediaMessage(msg) && msg.type !== 'audio' ? ' has-media' : ''
              }`}
            >
              <div className='message-sender'>
                {isOwnMessage ? 'You' : msg.senderName}
              </div>
              <div
                className='message-text'
                style={{ wordWrap: 'break-word', overflowWrap: 'break-word' }}
              >
                {isMediaMessage(msg) ? (
                  <MessageMedia msg={msg} />
                ) : (
                  formatMessageText(msg.text)
                )}
              </div>
              <div className='message-meta'>
                <span className='message-time'>
                {formatTime(msg.createdAt)}
                {isOwnMessage && msg.status && (
                  <span
                    className={`message-status ${
                      msg.status === 'failed' ? 'message-status-failed' : ''
                    }`}
                  >
                    {msg.status === 'sent' && '✓'}
                    {msg.status === 'delivered' && '✓✓'}
                    {msg.status === 'read' && '✓✓'}
                    {msg.status === 'sending' && isMediaMessage(msg) && 'Sending…'}
                    {msg.status === 'failed' &&
                      (msg.error && msg.error !== 'Could not send message'
                        ? `Not sent: ${msg.error}`
                        : 'Not sent. Check your connection.')}
                  </span>
                  )}
                </span>
                {canEmailThisMessage && (
                  <button
                    type='button'
                    className={`message-email-button ${
                      isMessageNotified ? 'notified' : ''
                    }`}
                    title={tooltip}
                    onClick={() => onSendNotification && onSendNotification(msg)}
                    disabled={notifyLoading}
                  >
                    <FiMail
                      size={16}
                      className={`message-email-icon ${
                        isMessageNotified ? 'notified' : 'default'
                      }`}
                    />
                  </button>
                )}
              </div>
            </div>
          </div>
        );
      })}
      <div ref={messagesEndRef} />
    </div>
  );
}

export default MessageList;
